import { router } from '@inertiajs/react';
import { useEffect } from 'react';

// Marks a background reload, so only its own failure skips the error dialog.
const QUIET_HEADER = 'X-Dgr-Quiet-Reload';

const isQuiet = config => {
    const headers = config?.headers;

    return Boolean(headers?.get?.(QUIET_HEADER) ?? headers?.[QUIET_HEADER]);
};

/**
 * Options for a background reload (a poll, a trail page) that fails quietly:
 * the page keeps the data it has and says the update failed, instead of
 * Inertia's error dialog over the whole monitor.
 */
export const quiet = ({ onSuccess, onFailure, headers, ...options } = {}) => {
    let succeeded = false;

    return {
        ...options,
        headers: { ...headers, [QUIET_HEADER]: '1' },
        onSuccess: page => {
            succeeded = true;
            onSuccess?.(page);
        },
        onFinish: visit => {
            if (!succeeded && !visit?.cancelled && !visit?.interrupted) {
                onFailure?.();
            }
        },
    };
};

/**
 * Suppresses Inertia's error dialog for quiet reloads only. Inertia 2 has no
 * per-visit callback for these, so the events are matched by the header.
 */
export const useQuietFailures = () =>
    useEffect(() => {
        const removeInvalid = router.on('invalid', event => {
            if (isQuiet(event.detail.response?.config)) {
                event.preventDefault();
            }
        });

        const removeException = router.on('exception', event => {
            if (isQuiet(event.detail.exception?.config)) {
                event.preventDefault();
            }
        });

        return () => {
            removeInvalid();
            removeException();
        };
    }, []);
